import jsPDF from 'jspdf';
import { Transaction, Account, Category, BudgetAllocation } from '../types';

// Simple PDF reports using jsPDF - only the three required reports
export class SimplePDFReports {
  
  // Report 1: Monthly Budget vs Actual
  generateMonthlyBudgetVsActual(
    budgetAllocations: BudgetAllocation[],
    categories: Category[],
    month: string
  ): Blob {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.text(`Monthly Budget vs Actual - ${month}`, 105, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 105, 35, { align: 'center' });

    let y = 50;

    // Table headers
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Category', 20, y);
    doc.text('Budgeted', 70, y);
    doc.text('Actual', 110, y);
    doc.text('Variance', 150, y);
    doc.text('Status', 180, y);

    y += 10;
    
    // Separator line
    doc.line(15, y, 195, y);
    y += 10;

    // Filter allocations for the month
    const monthAllocations = budgetAllocations.filter(a => a.month === month);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    let totalBudgeted = 0;
    let totalActual = 0;

    monthAllocations.forEach((allocation) => {
      if (y > 270) { // Page break
        doc.addPage();
        y = 30;
      }

      const category = categories.find(c => c.id === allocation.categoryId);
      const categoryName = category?.name || 'Unknown';
      const variance = allocation.allocated - Math.abs(allocation.spent);
      const isOverBudget = variance < 0;

      totalBudgeted += allocation.allocated;
      totalActual += Math.abs(allocation.spent);

      doc.text(this.truncateText(categoryName, 20), 20, y);
      doc.text(`$${allocation.allocated.toFixed(2)}`, 70, y);
      doc.text(`$${Math.abs(allocation.spent).toFixed(2)}`, 110, y);
      
      // Color code variance
      doc.setTextColor(isOverBudget ? 255 : 0, isOverBudget ? 0 : 128, 0);
      doc.text(`${isOverBudget ? '-' : ''}$${Math.abs(variance).toFixed(2)}`, 150, y);
      doc.text(isOverBudget ? 'OVER' : 'OK', 180, y);
      doc.setTextColor(0, 0, 0);
      
      y += 8;
    });

    // Totals
    y += 5;
    doc.line(15, y, 195, y);
    y += 10;

    const totalVariance = totalBudgeted - totalActual;
    doc.setFont('helvetica', 'bold');
    doc.text('TOTALS', 20, y);
    doc.text(`$${totalBudgeted.toFixed(2)}`, 70, y);
    doc.text(`$${totalActual.toFixed(2)}`, 110, y);
    
    doc.setTextColor(totalVariance < 0 ? 255 : 0, totalVariance < 0 ? 0 : 128, 0);
    doc.text(`${totalVariance < 0 ? '-' : ''}$${Math.abs(totalVariance).toFixed(2)}`, 150, y);
    doc.setTextColor(0, 0, 0);

    return new Blob([doc.output('blob')], { type: 'application/pdf' });
  }

  // Report 2: Cash-flow Summary  
  generateCashFlowSummary(
    transactions: Transaction[],
    accounts: Account[],
    month: string
  ): Blob {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.text(`Cash Flow Summary - ${month}`, 105, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 105, 35, { align: 'center' });

    let y = 50;

    // Filter transactions for the month
    const [year, monthNum] = month.split('-');
    const monthStart = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
    const monthEnd = new Date(parseInt(year), parseInt(monthNum), 0);

    const monthTransactions = transactions.filter(t => {
      const tDate = new Date(t.date);
      return tDate >= monthStart && tDate <= monthEnd;
    });

    // Calculate opening balances (transactions before month start)
    const openingBalances = new Map<string, number>();
    accounts.forEach(account => {
      const priorTransactions = transactions.filter(t => {
        const tDate = new Date(t.date);
        return t.accountId === account.id && tDate < monthStart;
      });
      const balance = priorTransactions.reduce((sum, t) => sum + t.amount, 0);
      openingBalances.set(account.id, balance);
    });

    // Calculate inflows and outflows
    let totalInflows = 0;
    let totalOutflows = 0;

    monthTransactions.forEach(t => {
      if (t.amount > 0) {
        totalInflows += t.amount;
      } else {
        totalOutflows += Math.abs(t.amount);
      }
    });

    // Summary section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Cash Flow Summary', 20, y);
    y += 20;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    const totalOpening = Array.from(openingBalances.values()).reduce((sum, bal) => sum + bal, 0);
    doc.text(`Opening Balance: $${totalOpening.toFixed(2)}`, 20, y);
    y += 15;

    doc.setTextColor(0, 128, 0);
    doc.text(`Total Inflows: $${totalInflows.toFixed(2)}`, 20, y);
    y += 10;

    doc.setTextColor(255, 0, 0);
    doc.text(`Total Outflows: $${totalOutflows.toFixed(2)}`, 20, y);
    y += 10;

    const netFlow = totalInflows - totalOutflows;
    const closingBalance = totalOpening + netFlow;

    doc.setTextColor(netFlow >= 0 ? 0 : 255, netFlow >= 0 ? 128 : 0, 0);
    doc.text(`Net Flow: ${netFlow >= 0 ? '$' : '-$'}${Math.abs(netFlow).toFixed(2)}`, 20, y);
    y += 15;

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(`Closing Balance: $${closingBalance.toFixed(2)}`, 20, y);
    y += 25;

    // Account breakdown
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Account Breakdown', 20, y);
    y += 15;

    doc.setFontSize(10);
    doc.text('Account', 20, y);
    doc.text('Opening', 70, y);
    doc.text('Inflows', 110, y);
    doc.text('Outflows', 150, y);
    doc.text('Closing', 180, y);
    y += 8;

    doc.line(15, y, 195, y);
    y += 8;

    accounts.forEach(account => {
      if (y > 270) { // Page break
        doc.addPage();
        y = 30;
      }

      const opening = openingBalances.get(account.id) || 0;
      const accountTransactions = monthTransactions.filter(t => t.accountId === account.id);
      const inflows = accountTransactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
      const outflows = accountTransactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
      const closing = opening + inflows - outflows;

      doc.setFont('helvetica', 'normal');
      doc.text(this.truncateText(account.name, 15), 20, y);
      doc.text(`$${opening.toFixed(2)}`, 70, y);
      doc.text(`$${inflows.toFixed(2)}`, 110, y);
      doc.text(`$${outflows.toFixed(2)}`, 150, y);
      doc.text(`$${closing.toFixed(2)}`, 180, y);
      y += 8;
    });

    return new Blob([doc.output('blob')], { type: 'application/pdf' });
  }

  // Report 3: Monthly Transactions
  generateMonthlyTransactions(
    transactions: Transaction[],
    accounts: Account[],
    categories: Category[],
    month: string
  ): Blob {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.text(`Monthly Transactions - ${month}`, 105, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 105, 35, { align: 'center' });

    let y = 50;

    // Filter transactions for the month
    const [year, monthNum] = month.split('-');
    const monthStart = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
    const monthEnd = new Date(parseInt(year), parseInt(monthNum), 0);

    const monthTransactions = transactions.filter(t => {
      const tDate = new Date(t.date);
      return tDate >= monthStart && tDate <= monthEnd;
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Table headers
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Date', 20, y);
    doc.text('Payee', 45, y);
    doc.text('Memo', 90, y);
    doc.text('Category', 130, y);
    doc.text('Amount', 170, y);
    y += 8;

    // Separator line
    doc.line(15, y, 195, y);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    let runningTotal = 0;

    monthTransactions.forEach((transaction) => {
      if (y > 275) { // Page break
        doc.addPage();
        y = 30;
        
        // Re-add headers on new page
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Date', 20, y);
        doc.text('Payee', 45, y);
        doc.text('Memo', 90, y);
        doc.text('Category', 130, y);
        doc.text('Amount', 170, y);
        y += 8;
        doc.line(15, y, 195, y);
        y += 8;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
      }

      const category = categories.find(c => c.id === transaction.categoryId);
      
      runningTotal += transaction.amount;

      doc.text(new Date(transaction.date).toLocaleDateString(), 20, y);
      doc.text(this.truncateText(transaction.payee, 15), 45, y);
      doc.text(this.truncateText(transaction.memo || '', 12), 90, y);
      doc.text(this.truncateText(category?.name || 'Uncategorized', 12), 130, y);
      
      // Color code amounts
      doc.setTextColor(transaction.amount >= 0 ? 0 : 255, transaction.amount >= 0 ? 128 : 0, 0);
      doc.text(`${transaction.amount >= 0 ? '$' : '-$'}${Math.abs(transaction.amount).toFixed(2)}`, 170, y);
      doc.setTextColor(0, 0, 0);
      
      y += 6;
    });

    // Summary
    y += 5;
    doc.line(15, y, 195, y);
    y += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Transactions: ${monthTransactions.length}`, 20, y);
    doc.text(`Net Amount: ${runningTotal >= 0 ? '$' : '-$'}${Math.abs(runningTotal).toFixed(2)}`, 130, y);

    return new Blob([doc.output('blob')], { type: 'application/pdf' });
  }

  private truncateText(text: string, maxLength: number): string {
    return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
  }
}

export const simplePdfReports = new SimplePDFReports();